/* -----------------------------------------------------------------------------------------------------
  - Created: [31-01-2025] by [SREERAJ]
  - Modified: [YYYY-MM-DD] 
  - Description: This file contains Leave Type list and modals
  -------------------------------------------------------------------------------------------------------*/
import React, { useEffect, useState } from 'react'
import CustomBottomPagination from '../../../../Components/custom_components/CustomBottomPagination/CustomBottomPagination'
import CustomTable from '../../../../Components/custom_components/CustomTable/CustomTable'
import CustomModal from '../../../../Components/custom_components/CustomModal/CustomModal'
import LeaveTypeForm from '../LeaveTypeForm/LeaveTypeForm'
import { ErrorToast, InfoToast, SuccessToast } from '../../../../utils/toast'
import CustomDeleteConfirmModal from '../../../../Components/custom_components/CustomDeleteConfirmModal/CustomDeleteConfirmModal'
import { handlePaginationEntries } from '../../../../utils/pagination'
import { get_leave_type_by_id, get_leave_types_with_pagination, leave_type_creation, leave_type_deletion, leave_type_update } from '../../../../config/SettingsApis/leaveTypeApis'
import LeaveTypeSearchFilter from '../LeaveTypeSearchFilter/LeaveTypeSearchFilter'
import { useSelector } from 'react-redux'
import { hasPrivilege } from '../../../../utils/privileges'
import Loader from '../../../../Components/Loader/Loader'
import { Icons } from '../../../../utils/GlobalIcons'
import CustomTooltipWithoutRoundBorder from '../../../../Components/CustomActionButtonWithTooltip/CustomTooltipWithoutRoundBorder'
import { getStatusColors } from '../../../../utils/constants'
function LeaveTypeTable() {
    const privileges = useSelector((state) => state.privileges.privileges);

    const [leaveTypesListing, setLeaveTypesListing] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);  // Current page number
    const [totalItemPerPage, setTotalItemPerPage] = useState(25);  // Items per page
    const [totalPages, setTotalPages] = useState(0);  // Total number of pages
    const [refresh, setRefresh] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false); // Loader state

    //  variables for both modals
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState({
        isOpen: false,
        id: '',
    });
    const [modalProps, setModalProps] = useState({
        isOpen: false,
        id: '',
        formValues: {}
    });

    // Pagination state
    const [pagination, setPagination] = useState({
        start: 0,
        end: 0,
        totalEntries: 0,
    });

    useEffect(() => {
        get_leave_types()
    }, [pageNumber, totalItemPerPage, refresh])


    // Get leave types list with pagination
    const get_leave_types = async () => {
        const formData = new FormData();
        formData.append('page', pageNumber);
        formData.append('pageItemcount', totalItemPerPage);

        try {
            setIsLoading(true)
            const response = await get_leave_types_with_pagination(formData)
            if (response?.status === "success") {
                setLeaveTypesListing(response.data.data)
                setTotalPages(response.data.last_page)
                const updatedPagination = handlePaginationEntries(response.data.from, response.data.to, response.data.total)
                setPagination(updatedPagination);
            } else {
                setLeaveTypesListing([])
            }
        } catch (error) {
            setLeaveTypesListing([])
        } finally {
            setIsLoading(false)
        }
    }

    // Define the required permission actions
    const PERMISSION_ACTIONS = {
        view: 4,
        edit: 5,
        history: 6,
        add: 3,
    };

    // Map permissions to boolean values based on privileges
    const userPermissions = {
        view: hasPrivilege(privileges, "Settings", 12, PERMISSION_ACTIONS.view),
        edit: hasPrivilege(privileges, "Settings", 12, PERMISSION_ACTIONS.edit),
        history: hasPrivilege(privileges, "Settings", 12, PERMISSION_ACTIONS.history),
        add: hasPrivilege(privileges, "Settings", 12, PERMISSION_ACTIONS.add),
    };

    // / Function to check if any action-related permissions are granted
    const hasActionPermissions = userPermissions.edit

    // FOR TABLE COLUMNS
    const columns = [
        {
            title: "#",
            key: "key",
        },
        {
            title: "Leave Type",
            key: "leaveType",
        },
        {
            title: "Description",
            key: "leaveTypeDescription",
        },
        {
            title: "COF",
            key: "is_cof",
        },
        {
            title: "Status",
            key: "status",
            render: (_, record) => {
                //  actual status text
                const statusText = record?.status || "Unknown";

                const statusCode = statusText.toLowerCase() === "active" ? 1 : statusText.toLowerCase() === "inactive" ? 2 : '';
                const style = getStatusColors(statusCode);

                return (
                    <span className="status-badge" style={style}>
                        {statusText}
                    </span>
                );
            },
        },
        { title: "Actions", key: "actions", render: (text, row) => renderActions(row) },
    ].filter(column => column.key !== "actions" || hasActionPermissions); //removes the "Actions" column when hasActionPermissions is false


    // Render custom actions based on user role
    const renderActions = (row) => {
        return (
            <div className='action-container'>
                {userPermissions.edit &&
                    <CustomTooltipWithoutRoundBorder title="Edit">
                        <Icons.Edit className="edit-action-icon" onClick={() => handleModal(true, row?.id)} />
                    </CustomTooltipWithoutRoundBorder>
                }
                {/* <FaTrashAlt className="delete-action-icon" onClick={() => handlDeleteModal(true, row?.id)} /> */}
            </div>

        );
    };

    // FOR TABLE ITEMS
    const datas = () => leaveTypesListing?.map((item, index) => ({
        key: pageNumber * totalItemPerPage - (totalItemPerPage - (index + 1)),
        id: item.LTL_Id,
        leaveType: item.Name,
        status: item?.statusName,
        statusId: item.LeaveTypeStatus,
        leaveTypeDescription: item.Description,
        is_cof: item?.is_cof === 2 ? "Yes" : "No",
    }));

    // FOR HANDLE ADD/EDIT MODAL OPEN
    const handleModal = async (isOpen, leaveTypeId) => {
        if (leaveTypeId) { // FOR EDIT
            const params = {
                leaveTypeID: leaveTypeId
            }
            GetLeaveTypeById(params)
        } else { // FOR ADD NEW LEAVE TYPE
            setModalProps({
                isOpen,
                id: '',
                formValues:
                {
                    Name: '',
                    status: 1,
                    Description: '',
                    is_cof: 1 // Default value for is_cof 1 (not COF)
                }
            })
        }
    };

    // FOR GET LEAVE TYPE DETAILS BY ID
    const GetLeaveTypeById = async (params) => {
        try {
            const response = await get_leave_type_by_id(params)
            if (response?.status === "success") {
                setModalProps({
                    isOpen: true,
                    id: response?.data?.LTL_Id,
                    formValues: // WHICH MEANS EDIT IS ACTIVE
                    {
                        Name: response?.data?.LTL_Name,
                        status: response?.data?.LTL_status,
                        Description: response?.data?.LTL_Description || "",
                        is_cof: response?.data?.is_cof || 1 // Default value for is_cof 1 (not COF)
                    }
                });
            } else {
                ErrorToast(response?.message || `Something Went Wrong!`)
            }
        } catch (error) {
            ErrorToast(`Something Went Wrong!`)
        }
    }

    // Function to handle HIDE/SHOW DELETE MODAL
    const handlDeleteModal = (isOpen, id = '') => {
        setIsDeleteModalOpen({ isOpen, id })
    }

    // FOR ADD/UPDATE LEAVE TYPE FUNCTION
    const handleLeaveTypeSave = async (values, leaveTypeId) => {
        const formData = new FormData();
        for (const key in values) {
            formData.append(key, values[key]);
        }

        try {
            setIsButtonDisabled(true);

            // Append leaveTypeId if updating
            if (leaveTypeId) {
                formData.append('leaveTypeId', leaveTypeId);
            }

            // Choose the appropriate API function based on leaveTypeId
            const apiFunction = leaveTypeId ? leave_type_update : leave_type_creation;
            const response = await apiFunction(formData);

            // Handle response
            const message = response?.data?.message || 'Something went wrong!';
            const status = response?.data?.status;

            if (status === "success") {
                SuccessToast(message);
                handleModal(false);
                setRefresh(!refresh);
            } else {
                ErrorToast(message);
            }
        } catch (error) {
            let errorMessage = 'Something went wrong!';
            // Check if error response contains dynamic errors
            if (error?.data?.errors) {
                for (const field in error.data.errors) {
                    if (error.data.errors[field] && error.data.errors[field].length > 0) {
                        errorMessage = error.data.errors[field][0];
                        break;  // want to stop after the first error field
                    }
                }
            }

            ErrorToast(errorMessage);
        } finally {
            setIsButtonDisabled(false);
        }
    };

    // FOR LEAVE TYPE DELETION
    const handleDeleteLeaveType = async () => {
        const params = {
            leaveTypeID: isDeleteModalOpen?.id
        }
        try {
            const response = await leave_type_deletion(params)

            if (response?.data?.status === "success") {
                SuccessToast(response?.data?.message)
                handlDeleteModal(false, '')
                setRefresh(!refresh)
            } else {
                ErrorToast(response?.data?.message || `Something Went Wrong!`)
            }
        } catch (error) {
            ErrorToast(`Something Went Wrong!`)
        }
    }

    // Update pagination
    const updatePagination = (pageNumber) => setPageNumber(pageNumber);
    const updateItemPageCount = (pageItemCount) => setTotalItemPerPage(pageItemCount);

    return (
        <div className="leave-type-table-container">
            <LeaveTypeSearchFilter tableTitle="Leave "
                isAddEdiModalOpen={modalProps.isOpen}
                handleAddEditModal={handleModal}
                isAddPermission={userPermissions?.add}

            />

            <CustomTable columns={columns} rows={isLoading ? [] : datas()} isLoading={isLoading} />
            {/* Pagination component */}
            {isLoading ? <Loader /> : <CustomBottomPagination
                items_per_page={totalItemPerPage}
                current_page={pageNumber}
                total_page_count={totalPages}
                on_change_value={updatePagination}
                on_page_count_update={updateItemPageCount}
                pagination={pagination}
            />}

            {/* Leave Type Add/Edit Modal */}
            {modalProps.isOpen && <CustomModal
                open={modalProps.isOpen}
                handleClose={() => handleModal(false)}
                title={modalProps.id !== '' ? "Edit Leave Type" : "Create Leave Type"}
                body={<LeaveTypeForm
                    isButtonDisabled={isButtonDisabled}
                    handleLeaveTypeSave={handleLeaveTypeSave}
                    id={modalProps.id}
                    formIntialValues={modalProps.formValues}
                />}
                maxWidth="1000px"
            />}


            {/* Leave Type Delete Modal */}
            {isDeleteModalOpen?.isOpen && <CustomDeleteConfirmModal
                module_name={'Leave Type'}
                open_modal={isDeleteModalOpen?.isOpen}
                close_modal={handlDeleteModal}
                confirm_function={handleDeleteLeaveType}
            />
            }
        </div>
    );
}

export default LeaveTypeTable;
